import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './SqlResultsPage.css';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

// Types for SQL Results
interface SqlRecord {
  id: string;
  peid?: number;
  line_item_title: string;
  project_name?: string;
  cost_item_name?: string;
  fiscal_year: number;
  fiscal_type: string;
  value: number;
  organization: string;
  account_title?: string;
  program?: string;
  domain?: string;
  supplier?: string;
  // Add more fields as needed from your SQL schema
}

interface SqlResultsState {
  peids: number[];
  searchQuery?: string;
  timestamp: number;
}

const SqlResultsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sqlResults, setSqlResults] = useState<SqlRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [resultsPerPage, setResultsPerPage] = useState(50);
  const [userPermissions, setUserPermissions] = useState<any>(null);
  const [filteredResults, setFilteredResults] = useState<SqlRecord[]>([]);
  const [paginatedResults, setPaginatedResults] = useState<SqlRecord[]>([]);
  const [frontendCurrentPage, setFrontendCurrentPage] = useState(1);
  const [frontendResultsPerPage, setFrontendResultsPerPage] = useState(25);
  const [columnFilters, setColumnFilters] = useState<{[key: string]: string}>({
    organization: '',
    line_item_title: '',
    project_name: '',
    cost_item_name: '',
    fiscal_year: '',
    fiscal_type: '',
    account_title: '',
    program: '',
    domain: '',
    supplier: ''
  });

  // State data will be stored in component state to avoid re-parsing
  const [stateData, setStateData] = useState<SqlResultsState | null>(null);

  // Parse state data once on component mount
  useEffect(() => {
    const getStateData = (): SqlResultsState | null => {
      // First try URL parameters (for new tab)
      const stateParam = searchParams.get('state');
      if (stateParam) {
        try {
          return JSON.parse(decodeURIComponent(stateParam));
        } catch (e) {
          console.error('Failed to parse state from URL:', e);
        }
      }
      // Fallback to React Router state (for same tab navigation)
      return location.state as SqlResultsState | null;
    };

    const parsedState = getStateData();
    setStateData(parsedState);
  }, [searchParams, location.state]);

  const peids = stateData?.peids || [];
  const searchQuery = stateData?.searchQuery || '';

  // Fetch user permissions on component mount
  useEffect(() => {
    fetchUserPermissions();
  }, []);

  const fetchUserPermissions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('=== DEBUG: User Info Response ===');
      console.log('Full response data:', response.data);
      console.log('User groups:', response.data.groups);
      console.log('User permissions:', response.data.permissions);
      console.log('Can download?', response.data.permissions?.can_download);
      console.log('================================');

      setUserPermissions(response.data.permissions);
    } catch (err) {
      console.error('Failed to fetch user permissions:', err);
    }
  };

  const applyColumnFilters = (data: SqlRecord[]) => {
    return data.filter(record => {
      for (const [key, filterValue] of Object.entries(columnFilters)) {
        if (filterValue) {
          const recordValue = (record as any)[key];
          if (!recordValue || !recordValue.toString().toLowerCase().includes(filterValue.toLowerCase())) {
            return false;
          }
        }
      }
      return true;
    });
  };

  const handleFilterChange = (column: string, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  const clearAllFilters = () => {
    setColumnFilters({
      organization: '',
      line_item_title: '',
      project_name: '',
      cost_item_name: '',
      fiscal_year: '',
      fiscal_type: '',
      account_title: '',
      program: '',
      domain: '',
      supplier: ''
    });
  };

  // Apply filters whenever sqlResults or columnFilters change
  useEffect(() => {
    const filtered = applyColumnFilters(sqlResults);
    setFilteredResults(filtered);
    setFrontendCurrentPage(1); // Reset to first page when filters change
  }, [sqlResults, columnFilters]);

  // Apply frontend pagination whenever filteredResults or pagination settings change
  useEffect(() => {
    const startIndex = (frontendCurrentPage - 1) * frontendResultsPerPage;
    const endIndex = startIndex + frontendResultsPerPage;
    const paginated = filteredResults.slice(startIndex, endIndex);
    setPaginatedResults(paginated);
  }, [filteredResults, frontendCurrentPage, frontendResultsPerPage]);

  useEffect(() => {
    if (!stateData) return; // Wait for state to be parsed
    
    // Check authentication first
    const token = localStorage.getItem('authToken');
    if (!token) {
      setError('Not authenticated. Please login first.');
      return;
    }
    
    if (peids.length === 0) {
      setError('No items selected. Please go back to search and select items.');
      return;
    }
    fetchSqlResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateData, currentPage, resultsPerPage]);

  const fetchSqlResults = async () => {
    if (peids.length === 0) return;

    setLoading(true);
    setError(null);

    try {
      // Get authentication token
      const token = localStorage.getItem('authToken');
      
      const response = await axios.post(`${API_BASE_URL}/api/sql-results`, {
        peids: peids,
        page: currentPage,
        size: resultsPerPage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSqlResults(response.data.results);
      setTotalCount(response.data.total_count);
    } catch (err: any) {
      let errorMessage = 'Failed to fetch SQL results';
      
      if (err.response?.status === 401) {
        errorMessage = 'Authentication failed. Please login again.';
        // Clear invalid token
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
      } else if (err.response?.status === 403) {
        errorMessage = 'Access denied. You do not have permission to access this data.';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      console.error('SQL fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      // Get authentication token
      const token = localStorage.getItem('authToken');
      
      const response = await axios.post(`${API_BASE_URL}/api/sql-results/export`, {
        peids: peids,
        format: 'csv'
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'budget-results.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (peids.length === 0) {
    return (
      <div className="sql-results-page">
        <div className="error-container">
          <h2>No Items Selected</h2>
          <p>Please go back to search and select items to view detailed SQL results.</p>
          <button className="btn-primary" onClick={() => navigate('/')}>
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sql-results-page">
      {/* Top Header Section */}
      <div className="sql-results-page-header">
        <div className="sql-results-header-top">
          <button 
            className="sql-results-back-button" 
            onClick={() => navigate('/')}
            title="Back to Search"
          >
            ← Back
          </button>
          <h1 className="sql-results-page-title">Sql Data</h1>
        </div>
        
        <div className="sql-results-header-meta">
          <div className="sql-results-summary">
            <span className="sql-results-selected-items">{peids.length} selected items</span>
            <span className="sql-results-separator">•</span>
            <span className="sql-results-total-records">{totalCount.toLocaleString()} total records</span>
            {searchQuery && (
              <>
                <span className="sql-results-separator">•</span>
                <span className="sql-results-search-query">From search: "{searchQuery}"</span>
              </>
            )}
          </div>
          
          <div className="header-actions">
            <button 
              className={`sql-results-download-btn ${!userPermissions?.can_download ? 'sql-results-download-btn-disabled' : ''}`} 
              onClick={handleExport}
              disabled={loading || !userPermissions?.can_download}
              title={!userPermissions?.can_download ? 'You do not have permission to download data' : 'Download CSV data'}
            >
              <span className="sql-results-download-icon">⬇</span>
              Download
              {process.env.NODE_ENV === 'development' && !userPermissions?.can_download && (
                <span className="sql-results-debug-text">(disabled)</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="sql-results-main-content">
        {/* Results Controls */}
        <div className="results-controls">
          <div className="pagination-info">
            Showing {Math.min((frontendCurrentPage - 1) * frontendResultsPerPage + 1, filteredResults.length)} to {Math.min(frontendCurrentPage * frontendResultsPerPage, filteredResults.length)} of {filteredResults.length} filtered results
            {filteredResults.length !== sqlResults.length && (
              <span className="filter-info"> (from {sqlResults.length} total)</span>
            )}
          </div>
          
          <div className="controls-group">
            {Object.values(columnFilters).some(filter => filter) && (
              <button className="clear-filters-btn" onClick={clearAllFilters}>
                Clear Filters
              </button>
            )}
            <label>Show:</label>
            <select 
              value={frontendResultsPerPage} 
              onChange={(e) => {
                setFrontendResultsPerPage(Number(e.target.value));
                setFrontendCurrentPage(1);
              }}
              className="per-page-select"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>per page</span>
          </div>
        </div>

      {/* Error State */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading detailed financial data...</p>
        </div>
      )}

      {/* Results Table */}
      {!loading && sqlResults.length > 0 && (
        <div className="sql-results-table-container">
          <table className="sql-results-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Line Item</th>
                <th>Project</th>
                <th>Cost Item</th>
                <th>Fiscal Year</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Account</th>
                <th>Program</th>
                <th>Domain</th>
                <th>Supplier</th>
              </tr>
              <tr className="filter-row">
                <th>
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.organization}
                    onChange={(e) => handleFilterChange('organization', e.target.value)}
                    className="column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.line_item_title}
                    onChange={(e) => handleFilterChange('line_item_title', e.target.value)}
                    className="column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.project_name}
                    onChange={(e) => handleFilterChange('project_name', e.target.value)}
                    className="column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.cost_item_name}
                    onChange={(e) => handleFilterChange('cost_item_name', e.target.value)}
                    className="column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.fiscal_year}
                    onChange={(e) => handleFilterChange('fiscal_year', e.target.value)}
                    className="column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.fiscal_type}
                    onChange={(e) => handleFilterChange('fiscal_type', e.target.value)}
                    className="column-filter"
                  />
                </th>
                <th>
                  {/* Amount column - no filter */}
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.account_title}
                    onChange={(e) => handleFilterChange('account_title', e.target.value)}
                    className="column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.program}
                    onChange={(e) => handleFilterChange('program', e.target.value)}
                    className="column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.domain}
                    onChange={(e) => handleFilterChange('domain', e.target.value)}
                    className="column-filter"
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filter..."
                    value={columnFilters.supplier}
                    onChange={(e) => handleFilterChange('supplier', e.target.value)}
                    className="column-filter"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedResults.map((record, index) => (
                <tr key={`${record.id}-${index}`}>
                  <td>{record.organization}</td>
                  <td className="line-item-title">{record.line_item_title}</td>
                  <td>{record.project_name || 'N/A'}</td>
                  <td>{record.cost_item_name || 'N/A'}</td>
                  <td>FY{record.fiscal_year}</td>
                  <td>{record.fiscal_type}</td>
                  <td className="amount-cell">{formatCurrency(record.value)}</td>
                  <td>{record.account_title || 'N/A'}</td>
                  <td>{record.program || 'N/A'}</td>
                  <td>{record.domain || 'N/A'}</td>
                  <td>{record.supplier || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

        {/* Frontend Pagination */}
        {!loading && filteredResults.length > frontendResultsPerPage && (
          <div className="pagination">
            <button 
              className="pagination-btn"
              onClick={() => setFrontendCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={frontendCurrentPage === 1}
            >
              ← Previous
            </button>
            
            <div className="pagination-numbers">
              {Array.from({ length: Math.ceil(filteredResults.length / frontendResultsPerPage) }, (_, i) => i + 1)
                .filter(page => {
                  const current = frontendCurrentPage;
                  return page === 1 || page === Math.ceil(filteredResults.length / frontendResultsPerPage) || 
                         (page >= current - 2 && page <= current + 2);
                })
                .map((page, index, pages) => (
                  <div key={page}>
                    {index > 0 && pages[index - 1] !== page - 1 && <span className="pagination-ellipsis">...</span>}
                    <button
                      className={`pagination-number ${frontendCurrentPage === page ? 'active' : ''}`}
                      onClick={() => setFrontendCurrentPage(page)}
                    >
                      {page}
                    </button>
                  </div>
                ))}
            </div>
            
            <button 
              className="pagination-btn"
              onClick={() => setFrontendCurrentPage(prev => prev + 1)}
              disabled={frontendCurrentPage >= Math.ceil(filteredResults.length / frontendResultsPerPage)}
            >
              Next →
            </button>
          </div>
        )}

        {/* No Results */}
        {!loading && sqlResults.length === 0 && !error && (
          <div className="no-results">
            <h3>No detailed records found</h3>
            <p>The selected items may not have detailed financial data available.</p>
          </div>
        )}
      </div> {/* Close sql-results-main-content */}
    </div>
  );
};

export default SqlResultsPage;